<template>
<div class="py-2 pl-4 user-row" :style="gridStyles" data-locator="user-row" :data-name="user.fullName">
  <span class="user-name text-text-secondary" data-locator="user-name">{{ user.fullName }}</span>

  <div v-if="hasSignersOrder" class="text-center">
    {{ isSignatory ? groupIndex : null }}
  </div>

  <template v-if="hasWitnesses">
    <div class="mx-auto">
      <AppTooltip v-if="isWitness" :disabled="!isWitnessOf">
        <template #activator="{props}">
          <div class="d-inline-flex" v-bind="props">
            <v-checkbox :model-value="isWitness"
                        density="compact"
                        readonly
                        hide-details
                        :disabled="true"
                        class="pt-0 mt-0 user-signer-checkbox"
            />
          </div>
        </template>
        <span>
          {{ $t('common.users.envelopeUsersSelector.EnvelopeUsersSelectorUser.witnessOf') }}
          <ul>
            <li v-for="(signer, i) in isWitnessOf"
                :key="`witnes-of-${i}`">
              {{ signer }}
            </li>
          </ul>
        </span>
      </AppTooltip>
    </div>
  </template>

  <template v-if="isForMultiEnvelope">
    <v-checkbox
      :model-value="flattenedSigners"
      density="compact"
      hide-details
      class="mx-auto pt-0 mt-0 user-signer-checkbox"
      data-locator="common-signer-checkbox"
      :value="user.id"
      :disabled="isMultiEnvelopeSignerDisabled"
      @update:model-value="$emit('toggle', {id: user.id, list: 'signers'})"
    >
    </v-checkbox>
    <v-checkbox
      :model-value="step.uniqueSigners"
      density="compact"
      hide-details
      class="mx-auto pt-0 mt-0 user-unique-signer-checkbox"
      data-locator="unique-signer-checkbox"
      :value="user.id"
      :disabled="isMultiEnvelopeUniqueSignerDisabled"
      @update:model-value="$emit('toggle', {id: user.id, list: 'uniqueSigners'})"
    >
    </v-checkbox>
  </template>
  <template v-else>
    <v-checkbox
      :model-value="flattenedSigners"
      density="compact"
      hide-details
      class="mx-auto pt-0 mt-0 user-signer-checkbox"
      data-locator="signer-checkbox"
      :value="user.id"
      :disabled="isSingleEnvelopeSignerDisabled"
      @update:model-value="$emit('toggle', {id: user.id, list: 'signers'})"
    />
  </template>

  <v-checkbox
    :model-value="step.validators"
    density="compact"
    hide-details
    class="mx-auto pt-0 mt-0 user-validator-checkbox"
    data-locator="validator-checkbox"
    :value="user.id"
    :disabled="isValidatorCheckboxDisabled"
    @update:model-value="$emit('toggle', {id: user.id, list: 'validators'})"
  />

  <v-checkbox
    :model-value="step.recipients"
    density="compact"
    hide-details
    class="mx-auto pt-0 mt-0 user-recipient-checkbox"
    data-locator="recipient-checkbox"
    :value="user.id"
    :disabled="observersOnly"
    @update:model-value="$emit('toggle', {id: user.id, list: 'recipients'})"
  />

  <v-checkbox
    :model-value="step.observers"
    density="compact"
    hide-details
    class="mx-auto pt-0 mt-0 user-observer-checkbox"
    data-locator="observer-checkbox"
    :value="user.id"
    :disabled="isObserverCheckboxDisabled"
    @update:model-value="$emit('toggle', {id: user.id, list: 'observers'})"
  />
</div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'

export default defineComponent({
  name: 'EnvelopeUsersSelectorUser',
  components: { AppTooltip },
  props: {
    user: {
      type: Object,
      required: true,
    },
    step: {
      type: Object,
      required: true,
    },
    recipientsAndObserversOnly: {
      type: Boolean,
      required: true,
    },
    enableSigners: {
      type: Boolean,
      required: true,
    },
    observersOnly: {
      type: Boolean,
      required: true,
    },
    enableValidators: {
      type: Boolean,
      required: true,
    },
    isPmGroup: {
      type: Boolean,
      required: true,
    },
    hasSignersOrder: {
      type: Boolean,
      required: true,
    },
    gridStyles: {
      type: Object,
      default: null,
      validator: (value) => { return typeof value === 'object' || value === null },
    },
    isForMultiEnvelope: {
      type: Boolean,
      required: true,
    },
    hasWitnesses: {
      type: Boolean,
      required: true,
    },
  },
  inject: ['canAddRecipient'],
  computed: {
    ...mapGetters('groups', ['allRoomMembers']),
    flattenedSigners () {
      return this.step?.signers?.flat()
    },
    isSignatory () {
      return this.flattenedSigners?.includes(this.user.id)
    },
    isWitness () {
      return this.step?.witnesses?.flatMap(w => w.witness).includes(this.user.id)
    },
    isWitnessOf () {
      if (this.isWitness) {
        const signerIds = this.step?.witnesses.filter(ws => ws.witness.includes(this.user.id)).map(s => s.signer)
        const signers = this.allRoomMembers.filter(s => signerIds.includes(s.id))
        return signers.map(s => s.fullName)
      }
      return null
    },
    groupIndex () {
      if (this.isSignatory) {
        const index = this.step.signers.findIndex(g => {
          return g.includes(this.user.id)
        })
        return index + 1
      }
      return null
    },
    isValidatorCheckboxDisabled () {
      return !this.enableValidators && (this.recipientsAndObserversOnly || this.observersOnly)
    },
    isObserverCheckboxDisabled () {
      return this.isSelected(this.user.id, 'signers')
        || this.isSelected(this.user.id, 'uniqueSigners')
        || this.isSelected(this.user.id, 'validators')
        || this.isWitness
        || this.isPmGroup
    },
    isSignerSelectionDisabled () {
      return !this.enableSigners
        || this.recipientsAndObserversOnly
        || this.observersOnly
        || this.isWitness
    },
    isSingleEnvelopeSignerDisabled () {
      return this.isSignerSelectionDisabled || this.willSelectingUserAsSignerExceedTheLimit
    },
    isMultiEnvelopeSignerDisabled () {
      const isUserCurrentlyCommonSigner = this.isSelected(this.user.id, 'uniqueSigners')
      return this.isSignerSelectionDisabled || isUserCurrentlyCommonSigner || this.willSelectingUserAsSignerExceedTheLimit
    },
    isMultiEnvelopeUniqueSignerDisabled () {
      const isUserCurrentlyUniqueSigner = this.isSelected(this.user.id, 'signers')
      return this.isSignerSelectionDisabled || isUserCurrentlyUniqueSigner
    },
    willSelectingUserAsSignerExceedTheLimit () {
      return !this.isSelected(this.user.id, 'signers') && !this.canAddRecipient
    },
  },
  methods: {
    isSelected (id, list) {
      if (list === 'signers') {
        return this.step?.signers?.some(group => {
          return group.some(userId => userId === id)
        })
      }
      return this.step[list]?.includes(id)
    },
  },
})
</script>

<style scoped lang="scss">
.user-row {
  display: grid;
  grid-gap: 10px;
}
</style>
